/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Select, MenuItem, FormControl } from "@mui/material";
import LabelledField from "components/labelled-field";
import { CardWebpageSelector } from "./webpage-selector";
import { CardLinkSelector } from "./link-selector";
import { CardDocumentSelector } from "./document-selector";
import { cloneObjectsArray } from "utils/array";

const TYPE_OPTIONS = [
    { value: "webpage", label: "Webpage" },
    { value: "link", label: "Link" },
    { value: "document", label: "Document" },
];

const CardEditor = React.memo((props) => {
    const card = props?.card;
    const index = props?.index;
    const cardsList = props?.cardsList;
    const cardsListRef = props?.cardsListRef;
    const updateCards = props?.updateCards;

    const _type = card?.type || "";
    const [type, setType] = useState(_type);

    // Sync type state when card.type changes externally
    useEffect(() => {
        if (_type !== type && _type !== undefined) {
            setType(_type);
        }
    }, [_type, type]);

    // Memoize the update function to avoid unnecessary re-renders
    const handleTypeUpdate = useCallback((newType) => {
        const currentCardsList = cardsListRef?.current || cardsList;
        if (currentCardsList && updateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            updatedCards[index] = {
                ...card,
                type: newType,
            };
            updateCards(updatedCards);
        }
    }, [cardsListRef, cardsList, updateCards, index, card]);

    useEffect(() => {
        // Only update if type has actually changed from the initial value
        if (type !== _type && type !== "") {
            handleTypeUpdate(type);
        }
    }, [type, _type, handleTypeUpdate]);

    const onChangeType = (event) => {
        setType(event.target.value);
    };

    const renderItemSelector = useMemo(() => {
        if (type === "webpage") {
            return (
                <CardWebpageSelector
                    card={card}
                    index={index}
                    cardsList={cardsList}
                    cardsListRef={cardsListRef}
                    updateCards={updateCards}
                />
            );
        } else if (type === "link") {
            return (
                <CardLinkSelector
                    card={card}
                    index={index}
                    cardsList={cardsList}
                    cardsListRef={cardsListRef}
                    updateCards={updateCards}
                />
            );
        } else if (type === "document") {
            return (
                <CardDocumentSelector
                    card={card}
                    index={index}
                    cardsList={cardsList}
                    cardsListRef={cardsListRef}
                    updateCards={updateCards}
                />
            );
        }
        return null;
    }, [type, card, index, cardsList, cardsListRef, updateCards]);

    return (
        <div className='simplicity-card-component-editor-card'>
            <div className='simplicity-card-component-editor-card-type'>
                <LabelledField label="Type">
                    <FormControl fullWidth>
                        <Select
                            value={type}
                            onChange={onChangeType}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>Select type</em>
                            </MenuItem>
                            {TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </LabelledField>
            </div>
            <div className='simplicity-card-component-editor-card-item'>
                {renderItemSelector}
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    return (
        prevProps.index === nextProps.index &&
        prevProps.card?.type === nextProps.card?.type &&
        prevProps.card?.webpage === nextProps.card?.webpage &&
        prevProps.card?.link === nextProps.card?.link &&
        prevProps.card?.document === nextProps.card?.document &&
        prevProps.card?.banner_image?.image === nextProps.card?.banner_image?.image &&
        prevProps.card?.title === nextProps.card?.title &&
        prevProps.card?.description === nextProps.card?.description &&
        prevProps.cardsList?.length === nextProps.cardsList?.length
    );
});

CardEditor.displayName = "CardEditor";
export { CardEditor };
